import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Select,
  TextInput,
  InputButton,
  Explainer,
  Paragraph,
  useNotification
} from '@sopranodesign/pulse-components';
import Icon from '@sopranodesign/pulse-icons';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  connectConfluence,
  deleteConfluenceSpace,
  saveConfluenceSpaces,
  resyncConfluenceSpace,
  resyncAllConfluence,
  disconnectConfluence,
  recrawlConfluencePage,
  saveConfluencePageTags,
  createConfluencePageTag,
  fetchConfluencePageTags,
  fetchConfluencePageAvailableTags
} from '../api';
import WarningMessageBody from './WarningMessageBody';
import TagModal from './TagModal';
import { useTagManager } from './useTagManager';

const ConfluenceModalManager = ({
  activeModal,
  modalProps,
  closeModal,
  spaces,
  setErrorIds,
  setSpaceLoadingIds,
  setPageLoadingIds,
  setIsSyncingAll
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [selectedSpaces, setSelectedSpaces] = React.useState([]);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const {
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    handleTagChange
  } = useTagManager({
    scope: 'generative_ai_confluence_pages',
    createTagFn: createConfluencePageTag
  });

  useEffect(() => {
    const openAndLoadTags = async () => {
      if (activeModal === 'tags' && modalProps?.page?.id) {
        try {
          const availableResp = await fetchConfluencePageAvailableTags();
          const availableTags = availableResp?.data?.data ?? [];
          setTags(availableTags);

          const assignedResp = await fetchConfluencePageTags(modalProps.page.id);
          const tagList = assignedResp?.data?.data ?? [];
          const ids = tagList.map(t => t.id);

          setSelectedTags(ids);
          setIsTagModalOpen(true);
        } catch (error) {
          showError(error.message || 'Failed to load tags.');
          setIsTagModalOpen(true);
        }
      }
    };

    openAndLoadTags();

    return () => {
      if (activeModal !== 'tags') {
        setSelectedTags([]);
        setIsTagModalOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, modalProps?.page?.id]);

  const toggleClientSecretVisibility = () => setShowClientSecret(prev => !prev);

  const handleConnect = async () => {
    if (!clientId || !clientSecret) {
      setConnectError('Please fill in all fields before connecting to Confluence.');
      return;
    }

    setConnectError('');
    setIsConnecting(true);

    // Open a blank tab immediately while still inside the click handler so
    // popup blockers treat it as a direct user gesture — the URL is only
    // known once the API call below resolves.
    const popup = window.open('', '_blank');

    try {
      const { data: redirectData } = await connectConfluence({ client_id: clientId, client_secret: clientSecret });
      popup.location.href = redirectData.authorize_url;
      closeModal();
    } catch (error) {
      popup.close();
      setConnectError(error.response?.data?.message ?? 'Unable to start the OAuth2 connection with Confluence.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSaveSpaces = async () => {
    try {
      const payload = selectedSpaces.map(space => ({
        id: space.value,
        key: space.key,
        name: space.label
      }));
      await saveConfluenceSpaces(payload);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/'] });
      showSuccess('Pages fetched successfully!');
      closeModal();
      setSelectedSpaces([]);
    } catch (error) {
      showError(`Failed to fetch pages: ${error.message}`);
    }
  };

  const confirmationActions = {
    delete: {
      header: 'Confirm Delete Space',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the space "${modalProps.space?.space_name}"?`}
          contentBody="All synced pages will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        const spaceId = modalProps.space.id;
        setSpaceLoadingIds(prev => ({ ...prev, [spaceId]: true }));
        try {
          await deleteConfluenceSpace(spaceId);
          showSuccess(`Space "${modalProps.space.space_name}" delete requested`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to delete space: ${error.message}`);
        } finally {
          setSpaceLoadingIds(prev => ({ ...prev, [spaceId]: false }));
        }
      }
    },
    disconnect: {
      header: 'Confirm Disconnect Confluence',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to disconnect Confluence?"
          contentBody="All synced spaces and pages will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Disconnect',
      handleConfirm: async () => {
        try {
          await disconnectConfluence();
          showSuccess('Confluence disconnect requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to disconnect Confluence: ${error.message}`);
        }
      }
    },
    resync: {
      header: 'Confirm Resync Space',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to resync the space "${modalProps.space?.space_name}"?`}
          contentBody="This will refresh its content."
        />
      ),
      confirmText: 'Resync',
      handleConfirm: async () => {
        const spaceId = modalProps.space.id;
        setSpaceLoadingIds(prev => ({ ...prev, [spaceId]: true }));
        try {
          await resyncConfluenceSpace(spaceId);
          showSuccess('Space resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/pages', spaceId] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync space: ${error.message}`);
        } finally {
          setSpaceLoadingIds(prev => ({ ...prev, [spaceId]: false }));
        }
      }
    },
    resyncAll: {
      header: 'Confirm Resync All Spaces',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to resync all spaces?"
          contentBody="This will refresh all spaces' content."
        />
      ),
      confirmText: 'Resync All',
      handleConfirm: async () => {
        setIsSyncingAll(true);
        try {
          await resyncAllConfluence();
          showSuccess('Spaces resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync spaces: ${error.message}`);
        } finally {
          setIsSyncingAll(false);
        }
      }
    },
    recrawl: {
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the page "${modalProps.page?.title}"?`}
          contentBody="This will update its content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        const pageId = modalProps.page.id;
        const spaceId = modalProps.spaceId;

        setErrorIds(prev => {
          const next = { ...prev };
          delete next[pageId];
          return next;
        });
        setPageLoadingIds(prev => ({ ...prev, [pageId]: true }));

        try {
          await recrawlConfluencePage(pageId);
          showSuccess('Page recrawl requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/pages', spaceId] });
        } catch (error) {
          if (error.response?.status >= 500) {
            setErrorIds(prev => ({ ...prev, [pageId]: 'Failed to recrawl the page.' }));
          }
          showError(`Failed to recrawl page: ${error.message}`);
        } finally {
          setPageLoadingIds(prev => ({ ...prev, [pageId]: false }));
          closeModal();
        }
      }
    }
  };

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'connect':
      return (
        <Modal
          header="Connect Confluence"
          handleCancel={closeModal}
          handleConfirm={handleConnect}
          confirmText="Connect"
          cancelText="Cancel"
          loading={isConnecting}
          body={
            <Flex col gap="sm">
              {connectError && (
                <Explainer type="danger" mb hideLabel>
                  <Paragraph sm danger>
                    {connectError}
                  </Paragraph>
                </Explainer>
              )}

              <Flex fat col>
                <Label htmlFor="confluenceClientId">OAuth Client ID</Label>
                <TextInput
                  id="confluenceClientId"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                />
              </Flex>

              <Flex fat col>
                <Label htmlFor="confluenceClientSecret">OAuth Client Secret</Label>
                <Flex relative>
                  <TextInput
                    id="confluenceClientSecret"
                    type={showClientSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)}
                  />
                  <InputButton type="button" onClick={toggleClientSecretVisibility}>
                    <Icon
                      size="1.25rem"
                      width="1.25rem"
                      height="1.25rem"
                      type={showClientSecret ? 'eye' : 'eyeOff'}
                    />
                  </InputButton>
                </Flex>
              </Flex>
            </Flex>
          }
        />
      );

    case 'spaces':
      return (
        <Modal
          header="Confluence Spaces"
          handleCancel={closeModal}
          handleConfirm={handleSaveSpaces}
          confirmText="Select"
          cancelText="Cancel"
          disableConfirmButton={selectedSpaces.length === 0}
          body={
            <Flex col gap="sm">
              <Label htmlFor="spaceSelect">Space</Label>
              <Select
                id="spaceSelect"
                value={selectedSpaces}
                onChange={setSelectedSpaces}
                options={spaces.map(space => ({ label: space.name, value: space.id, key: space.key }))}
                placeholder="Select a space"
                isMulti
              />
            </Flex>
          }
        />
      );

    case 'tags':
      return (
        <TagModal
          isVisible={isTagModalOpen}
          header="Confluence Page Tags"
          onCancel={() => {
            setIsTagModalOpen(false);
            setSelectedTags([]);
            closeModal();
          }}
          onConfirm={async () => {
            try {
              await saveConfluencePageTags({
                pageId: modalProps.page.id,
                groupIds: selectedTags
              });

              queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/pages', modalProps.spaceId] });

              setIsTagModalOpen(false);
              setSelectedTags([]);
              showSuccess('Tags assigned successfully!');
              closeModal();
            } catch (error) {
              showError(error.message || 'Failed to assign tags.');
            }
          }}
          tags={tags}
          selectedTags={selectedTags}
          handleTagChange={handleTagChange}
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={config.handleCancel ?? closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

ConfluenceModalManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  spaces: PropTypes.array,
  setErrorIds: PropTypes.func.isRequired,
  setSpaceLoadingIds: PropTypes.func.isRequired,
  setPageLoadingIds: PropTypes.func.isRequired,
  setIsSyncingAll: PropTypes.func.isRequired
};

export default ConfluenceModalManager;
